/**
 * Browser tab titles for Inertia pages.
 *
 * The layout renders a plain <title> that Inertia does not manage (it has no
 * data-inertia attribute), so nothing overwrites what we set here. Titles are
 * applied centrally on each navigation rather than by a <Head> in every page
 * component, which keeps new pages working without touching this file -- as
 * long as they follow the naming convention below.
 *
 * Convention: a page's component basename, first letter lowercased, is its i18n
 * section. Suppliers -> suppliers.title -> "Fornecedores". Pages that break the
 * convention, or that share a section with another page, are listed in
 * TITLE_KEY_OVERRIDES.
 */
import i18n from "@/lib/i18n"

const APP_NAME = "BierTrack"
const SEPARATOR = " · "

const TITLE_KEY_OVERRIDES: Record<string, string> = {
  // Detail and sub-pages that belong to a parent section.
  InventoryShow: "inventory.title",
  InventoryEvents: "inventory.title",
  UsersDisabled: "users.title",
  ContractNew: "contracts.title",
  // Sections whose name does not match the component name.
  ContractPreview: "contracts.preview.title",
  OrderPreview: "order.preview.title",
  "accounts/New": "createAccount.title",
}

function titleKeyFor(component: string): string {
  const override = TITLE_KEY_OVERRIDES[component]
  if (override) return override

  const basename = component.split("/").pop() ?? component
  return `${basename.charAt(0).toLowerCase()}${basename.slice(1)}.title`
}

/**
 * The tab title for an Inertia component name, e.g. "Inventário · BierTrack".
 *
 * Falls back to the bare app name when the page has no title key (Home, the
 * mobile shell) and when the key resolves to the app name itself (login.title
 * is "BierTrack"), so the name is never doubled up.
 */
export function pageTitleFor(component: string): string {
  const key = titleKeyFor(component)
  if (!i18n.exists(key)) return APP_NAME

  const name = i18n.t(key)
  if (!name || name === APP_NAME) return APP_NAME

  return `${name}${SEPARATOR}${APP_NAME}`
}

/** Set the tab title for the page being shown. */
export function applyPageTitle(component: string): void {
  document.title = pageTitleFor(component)
}
